import {emotionStyled, tokens} from '@packages/utilities/styled';
import {
  activeSelector,
  errorSelector,
  focusedSelector,
  hoverSelector,
} from '../../../utils';
import {NUMBER_INPUT_VARIANTS} from '../_constants';

const beforeStyles = {
  content: '""',
  position: 'absolute',
  top: '0.5px',
  right: '0.5px',
  bottom: '0.5px',
  left: '0.5px',
  pointerEvents: 'none',
  borderRadius: 'inherit',
} as const;

// where outline is set
export const NumberInputValueWrapperView = emotionStyled('div')(
  {
    background: `$uiInputBackground`,
    color: '$textPrimary',
    display: 'flex',
    padding: 0,
    outline: '$uiNumberInputValueWrapperOutline',
    outlineOffset: '-1px',
    height: tokens.ui.numberInput.input.height,
    '&::placeholder': {
      color: '$textDimmed',
    },
    input: {
      borderRadius: '$2',
    },
  },
  {
    componentName: 'NumberInputValueWrapper',
    variants: {
      variant: {
        [NUMBER_INPUT_VARIANTS.DEFAULT]: {
          borderRadius: '$2',
          boxShadow: '$inputInner',
          width: '100%',
          height: tokens.ui.numberInput.input.height,
          position: 'relative',

          [`&${hoverSelector}:not([data-disabled="true"]), ${activeSelector}`]:
            {
              outline: '$uiNumberInputValueWrapperOutlineHover',
              outlineOffset: '-1px',
              input: {
                borderColor: '$border3',
              },
            },
          [`&${focusedSelector}:not(${errorSelector})`]: {
            '::before': {
              ...beforeStyles,
              outline: '$blueBorder',
            },
            outline: 'none',
            input: {
              borderColor: 'transparent',
            },
          },
          [`&${errorSelector}`]: {
            '::before': {
              ...beforeStyles,
              outline: '$redBorder',
            },
            outline: 'none',
            input: {
              borderColor: 'transparent',
            },
          },
        },
        [NUMBER_INPUT_VARIANTS.STEPPER]: {
          borderRadius: '0',
          borderTop: '$uiNumberInputStepperInputBorder',
          borderBottom: '$uiNumberInputStepperInputBorder',
          width: tokens.ui.numberInput.stepper.input.width,
          outline: 'none',
          input: {
            borderRadius: '0',
            border: 'none',
          },
        },
      },
      isDisabled: {
        true: {
          opacity: tokens.opacities.disabled,
          cursor: 'not-allowed',
          '*': {
            cursor: 'not-allowed',
          },
        },
      },
      hasPrefix: {
        true: {
          input: {
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0,
            borderLeft: 'none',
            minWidth: tokens.ui.numberInput.input.withPrefixOrSuffix.minWidth,
          },
        },
      },
      hasSuffix: {
        true: {
          input: {
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0,
            borderRight: 'none',
            minWidth: tokens.ui.numberInput.input.withPrefixOrSuffix.minWidth,
          },
        },
      },
      shouldShowTrailingControls: {
        true: {
          input: {
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0,
            borderRight: 'none',
          },
        },
      },
    },
  }
);

// where border is set
export const NumberInputValueView = emotionStyled('input')(
  {
    appearance: 'none',
    background: `$uiInputBackground`,
    borderRadius: '$2 0 0 $2',
    outline: 'none',
    border: 'none',
    fontFamily: '$sansSerif',
    fontSize: '$body',
    lineHeight: '$body',
    padding: tokens.ui.numberInput.input.padding,
    resize: 'none',
    color: '$textPrimary',
    display: 'block',
    flex: 1,
    ':-webkit-autofill': {
      // These properties are necessary to make the user agent styles for autofill disappear (like turning the input background yellow and changing the text color)
      backgroundClip: 'text',
      WebkitTextFillColor: tokens.colors.textPrimary,
      caretColor: tokens.colors.textPrimary,
    },
  },
  {
    componentName: 'NumberInputValue',
    variants: {
      variant: {
        [NUMBER_INPUT_VARIANTS.DEFAULT]: {
          border: '$border2',
          width: '100%',
        },
        [NUMBER_INPUT_VARIANTS.STEPPER]: {
          border: 'transparent',
          textAlign: 'center',
          width: '100%',
        },
      },
    },
  }
);
